<template>
  <div v-if="!communityUsers" class="text-center">
    <p>{{ $t("views.profile.communities.loading.error") }}</p>
  </div>
  <div v-else>
    <template v-if="user.invitations.length > 0">
      <h3>{{ $t("views.profile.communities.invitations_pending") }}</h3>

      <email-not-verified-alert v-if="!user.email_verified_at" />

      <b-card
        v-for="invitation in user.invitations"
        :key="invitation.id"
        class="profile-communities__community profile-communities__community--invitation mb-3"
      >
        <div class="profile-communities__community_content">
          <div class="profile-communities__community_header">
            <h4 class="mb-0">{{ invitation.community.name }}</h4>

            <div class="profile-communities__subtitle">
              <span>
                {{
                  invitation.community.type === "borough"
                    ? $t("views.profile.communities.community.type.public")
                    : $t("views.profile.communities.community.type.private")
                }}
              </span>
            </div>
          </div>
          <div class="profile-communities__tags">
            <div class="profile-communities__tags__item">
              <span class="profile-communities__tags__item__label">{{
                $t("communities.fields.user.join_method") | capitalize
              }}</span>
              <span>
                <b-badge variant="primary">
                  {{ $t("communities.fields.user.join_method_labels.invitation") }}
                </b-badge>
              </span>
            </div>
          </div>
        </div>
        <div class="profile-communities__community_actions">
          <span>
            <icon-button
              class="mr-3"
              :disabled="!user.email_verified_at"
              icon="check"
              variant="success"
              @click="acceptInvitation(invitation)"
            >
              {{ $t("views.profile.communities.buttons.accept") }}
            </icon-button>
            <icon-button
              :disabled="!user.email_verified_at || invitationLoading"
              icon="x"
              variant="warning"
              @click="ignoreInvitation(invitation)"
            >
              {{ $t("views.profile.communities.buttons.ignore") }}
            </icon-button>
          </span>
        </div>
      </b-card>

      <hr />
    </template>
    <b-card v-else-if="communityUsers.length === 0" class="profile-communities__nocommunities">
      <div>
        <h3>{{ $t("views.profile.communities.no_communities.title") }}</h3>
      </div>
      <b-card-body>
        <p>
          {{ $t("views.profile.communities.no_communities.description") }}
        </p>
        <icon-button variant="primary" to="/profile/locomotion">
          {{ $t("views.profile.communities.buttons.edit_address") }}
        </icon-button>
      </b-card-body>
    </b-card>

    <b-card
      v-for="communityUser in communityUsers"
      :key="communityUser.community_id + '-' + communityUser.join_method"
      :class="['profile-communities__community', 'mb-3', communityUser.id > 0 ? 'clickable' : '']"
      @click="communityUserClick(communityUser)"
    >
      <div class="profile-communities__community_content">
        <div class="profile-communities__community_header">
          <h4 class="mb-0">{{ communityUser.community.name }}</h4>

          <div class="profile-communities__subtitle">
            <span>
              {{
                communityUser.community.type === "borough"
                  ? $t("views.profile.communities.community.type.public")
                  : $t("views.profile.communities.community.type.private")
              }}
            </span>
            &bull;
            <span>
              {{ communityUser.community.approved_users_count }}
              {{ $t("views.profile.communities.community.members") }}
            </span>
          </div>
        </div>

        <ul
          v-if="
            communityUser.community.starting_guide_url ||
            communityUser.community.chat_group_url ||
            communityUser.community.contact_email
          "
          class="profile-communities__resources__list"
        >
          <li
            v-if="communityUser.community.contact_email"
            class="community-overview__community__link"
          >
            <a target="_blank" :href="`mailto:${communityUser.community.contact_email}`">
              <b-icon class="mr-1" icon="envelope-open" />
              {{ communityUser.community.contact_email }}
            </a>
          </li>
          <li v-if="communityUser.community.starting_guide_url">
            <a :href="communityUser.community.starting_guide_url" target="_blank">
              <b-icon icon="clipboard-check" class="mr-1" />
              {{ $t("views.profile.communities.community.resources.guide") }}
            </a>
          </li>
          <li v-if="communityUser.community.chat_group_url">
            <a :href="communityUser.community.chat_group_url" target="_blank">
              <b-icon icon="chat-dots" class="mr-1" />
              {{ $t("views.profile.communities.community.resources.chat") }}
            </a>
          </li>
        </ul>

        <div class="profile-communities__tags">
          <div class="profile-communities__tags__item">
            <span class="profile-communities__tags__item__label">{{
              $t("communities.fields.user.join_method") | capitalize
            }}</span>
            <span>
              <b-badge v-if="communityUser.join_method === 'geo'" variant="success">
                {{ $t("communities.fields.user.join_method_labels.geo") | capitalize }}
              </b-badge>
              <b-badge v-if="communityUser.join_method === 'manual'" variant="warning">
                {{ $t("communities.fields.user.join_method_labels.manual") | capitalize }}
              </b-badge>
              <b-badge v-if="communityUser.join_method === 'invitation'" variant="primary">
                {{ $t("communities.fields.user.join_method_labels.invitation") | capitalize }}
              </b-badge>
              <b-badge v-if="communityUser.join_method === 'friend'" variant="info">
                {{ $t("communities.fields.user.join_method_labels.friend") | capitalize }}
              </b-badge>
            </span>
          </div>

          <div class="profile-communities__tags__item">
            <span class="profile-communities__tags__item__label">{{
              $t("communities.fields.user.status") | capitalize
            }}</span>
            <span>
              <b-badge v-if="communityUser.status === 'approved'" variant="success">
                {{ $t("communities.fields.user.status_labels.approved") | capitalize }}
              </b-badge>
              <b-badge v-else-if="communityUser.status === 'suspended'" variant="danger">
                <b-icon icon="exclamation-triangle" />
                {{ $t("communities.fields.user.status_labels.suspended") | capitalize }}
              </b-badge>
              <b-badge v-else-if="communityUser.proof_state === 'absent'" variant="danger">
                {{ $t("communities.fields.user.proof_labels.absent") | capitalize }}
              </b-badge>
              <b-badge v-else-if="communityUser.proof_state === 'partial'" variant="warning">
                {{ $t("communities.fields.user.proof_labels.partial") | capitalize }}
              </b-badge>
              <b-badge v-else-if="communityUser.proof_state === 'unevaluated'" variant="warning">
                {{ $t("communities.fields.user.proof_labels.submitted") | capitalize }}
              </b-badge>
              <b-badge v-else-if="communityUser.proof_state === 'invalid'" variant="danger">
                {{ $t("communities.fields.user.proof_labels.to_correct") | capitalize }}
              </b-badge>
            </span>
          </div>

          <div
            v-if="
              communityUser.current_paid_subscription || communityUser.current_granted_subscription
            "
            class="profile-communities__tags__item"
          >
            <template v-if="communityUser.current_granted_subscription">
              <span class="profile-communities__tags__item__label">{{
                $t("views.profile.communities.community.subscription.free.label")
              }}</span>
              <b-badge variant="success">
                {{ $t("views.profile.communities.community.subscription.free.until") }}
                {{ communityUser.current_granted_subscription.end_date | date }}
              </b-badge>
            </template>
            <template v-else>
              <span class="profile-communities__tags__item__label">{{
                $t("views.profile.communities.community.subscription.contribution.label")
              }}</span>
              <b-badge variant="success">
                {{ $t("views.profile.communities.community.subscription.contribution.until") }}
                {{ communityUser.current_paid_subscription.end_date | date }}
              </b-badge>
            </template>
          </div>
        </div>
      </div>
      <div class="profile-communities__community_actions">
        <!-- Display if communityUser is from the database (not a generated item from friendship) -->
        <icon-button
          v-if="communityUser.id > 0"
          variant="ghost-secondary"
          :icon="communityHasContribution(communityUser) ? 'three-dots' : 'person-badge'"
          :to="`/profile/communities/${communityUser.community.id}`"
        >
          {{
            communityHasContribution(communityUser)
              ? $t("views.profile.communities.buttons.details")
              : $t("views.profile.communities.buttons.proofs")
          }}
        </icon-button>
      </div>
    </b-card>
  </div>
</template>

<script>
import EmailNotVerifiedAlert from "@/components/shared/EmailNotVerifiedAlert.vue";
import IconButton from "@/components/shared/IconButton.vue";

export default {
  name: "ProfileCommunity",
  components: {
    EmailNotVerifiedAlert,
    IconButton,
  },
  data() {
    return {
      invitationLoading: false,
    };
  },
  computed: {
    user() {
      return this.$store.state.user;
    },
    communityUsers() {
      return this.$store.state.user.accessible_communities;
    },
  },
  methods: {
    async acceptInvitation({ id }) {
      try {
        this.invitationLoading = true;
        this.$store.dispatch("invitation/acceptById", { id });
      } finally {
        this.invitationLoading = false;
      }
    },
    communityHasContribution(communityUser) {
      return (
        communityUser.status === "approved" &&
        (communityUser.new_subscription_pricings ||
          communityUser.current_granted_subscription ||
          communityUser.current_paid_subscription)
      );
    },
    communityUserClick(communityUser) {
      if (communityUser.id > 0) {
        this.$router.push(`/profile/communities/${communityUser.community.id}`);
      }
    },
    async ignoreInvitation({ id }) {
      try {
        this.invitationLoading = true;
        this.$store.dispatch("invitation/refuseById", { id });
      } finally {
        this.invitationLoading = false;
      }
    },
  },
};
</script>

<style lang="scss">
@import "~bootstrap/scss/mixins/breakpoints";
.profile-communities {
  &__headers {
    padding-left: 15px;

    h4 {
      color: grey;
      font-size: 16px;
    }
  }

  &__community {
    box-shadow: $small-shadow;
    transition: box-shadow 0.2s ease-in-out;
    &:hover {
      box-shadow: $medium-shadow;
    }

    &--invitation {
      cursor: default;
      transition: none;
      &:hover {
        box-shadow: $small-shadow;
      }
    }
  }

  &__community.clickable {
    cursor: pointer;
  }

  &__nocommunities {
    .btn {
      margin-left: 0;
    }
  }

  &__tags {
    display: flex;
    flex-direction: row;
    gap: 1rem;
  }

  &__community .card-body {
    display: flex;
    flex-direction: row;
    gap: 1rem;
    justify-content: space-between;

    @include media-breakpoint-down(md) {
      flex-direction: column;
      align-items: flex-start;
    }

    @include media-breakpoint-down(sm) {
      align-items: stretch;
    }
  }

  &__community_content {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    justify-content: space-between;
    flex: 1;
  }

  &__community_header {
    display: flex;
    flex-direction: column;
  }

  &__subtitle {
    font-size: 0.8rem;
    color: $text-muted;
  }

  &__resources__list {
    list-style: none;
    padding: 0;
    margin: 0;
    li {
      margin-bottom: 0.25rem;
    }
  }

  &__tags {
    display: flex;
    flex-direction: row;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  &__tags__item {
    border: 1px solid $light-grey;
    border-radius: 0.25rem;
    padding: 0.25rem 0.5rem;
    display: flex;
    flex-direction: row;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  &__community_actions {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
  }
}
</style>
